import type { ParentKey } from '~/client/src/shared/utils/hierarchyTree'
import {
  buildOptionsByValue,
  canPlaceSubtree,
  getDescendantValues,
  getParentValues,
  MAX_HIERARCHY_DEPTH,
  resolveParentKey,
  ROOT_PARENT,
} from '~/client/src/shared/utils/hierarchyTree'

import {
  makeUniquePropertyName,
  normalizePropertyName,
} from '../../propertyName'
import { HierarchyOption, HierarchyRow } from './HierarchyPropertyConfig.types'

const createPathKey = (
  parentValue: string | undefined,
  label: string,
): string => JSON.stringify([parentValue ?? ROOT_PARENT, label])

export const createUniqueHierarchyOptionValue = (
  label: string,
  options: Pick<HierarchyOption, 'value'>[],
): string => {
  const baseValue = normalizePropertyName(label) || 'option'
  return makeUniquePropertyName(
    baseValue,
    new Set(options.map(option => option.value)),
  )
}

const parseIndentedLine = (line: string): { depth: number; label: string } => {
  let value = line
  let depth = 0

  const tabMatch = value.match(/^\t+/)
  if (tabMatch) {
    depth += tabMatch[0].length
    value = value.slice(tabMatch[0].length)
  }

  const spaceMatch = value.match(/^ +/)
  if (spaceMatch) {
    depth += Math.floor(spaceMatch[0].length / 2)
    value = value.slice(spaceMatch[0].length)
  }

  let matchedIndent = true
  while (matchedIndent) {
    matchedIndent = false
    const arrowMatch = value.match(/^\s*(?:→|>|-{1,2}>)\s*\|?\s*/)
    if (arrowMatch) {
      depth++
      value = value.slice(arrowMatch[0].length)
      matchedIndent = true
    }
  }

  value = value.replace(/^\s*[|•*–—-]\s+/, '').trim()

  return { depth, label: value }
}

const createExistingPathOptions = (
  existingOptions: HierarchyOption[],
): Map<string, HierarchyOption> => {
  const pathOptions = new Map<string, HierarchyOption>()

  const liveFirst = [
    ...existingOptions.filter(option => !option.deleted),
    ...existingOptions.filter(option => option.deleted),
  ]

  liveFirst.forEach(option => {
    const pathKey = createPathKey(option.parentValue, option.label)
    if (!pathOptions.has(pathKey)) {
      pathOptions.set(pathKey, option)
    }
  })

  return pathOptions
}

const addBulkOption = ({
  label,
  parentValue,
  options,
  existingOptions,
  pathOptions,
  revivedValues,
}: {
  label: string
  parentValue?: string
  options: HierarchyOption[]
  existingOptions: HierarchyOption[]
  pathOptions: Map<string, HierarchyOption>
  revivedValues: Set<string>
}): HierarchyOption => {
  const pathKey = createPathKey(parentValue, label)
  const existingPathOption = pathOptions.get(pathKey)
  if (existingPathOption) {
    if (existingPathOption.deleted) {
      revivedValues.add(existingPathOption.value)
    }
    return existingPathOption
  }

  const option: HierarchyOption = {
    value: createUniqueHierarchyOptionValue(label, [
      ...existingOptions,
      ...options,
    ]),
    label,
    parentValue,
    isNew: true,
  }

  options.push(option)
  pathOptions.set(pathKey, option)

  return option
}

export const parseBulkHierarchyOptions = (
  text: string,
  existingOptions: HierarchyOption[],
  maxDepth = MAX_HIERARCHY_DEPTH,
): { options: HierarchyOption[]; revivedValues: Set<string> } => {
  const parsedOptions: HierarchyOption[] = []
  const stack: HierarchyOption[] = []
  const pathOptions = createExistingPathOptions(existingOptions)
  const revivedValues = new Set<string>()

  for (const line of text.split(/\r?\n/)) {
    if (!line.trim()) continue

    const cells = line.split('\t').map(cell => cell.trim())
    const nonEmptyCells = cells
      .map((label, index) => ({ label, index }))
      .filter(cell => cell.label)

    // Tab-separated rows (pasted spreadsheet columns): a cell's column index is
    // its depth, so leading blank columns still nest under the correct parent.
    if (cells.length > 1) {
      for (const cell of nonEmptyCells) {
        const depth = Math.min(cell.index, stack.length, maxDepth)
        const parent = depth > 0 ? stack[depth - 1] : undefined
        const option = addBulkOption({
          label: cell.label,
          parentValue: parent?.value,
          options: parsedOptions,
          existingOptions,
          pathOptions,
          revivedValues,
        })
        stack[depth] = option
        stack.length = depth + 1
      }
      continue
    }

    const parsedLine = parseIndentedLine(line)
    if (!parsedLine.label) continue

    const depth = Math.max(
      0,
      Math.min(parsedLine.depth, stack.length, maxDepth),
    )
    const parent = depth > 0 ? stack[depth - 1] : undefined
    const option = addBulkOption({
      label: parsedLine.label,
      parentValue: parent?.value,
      options: parsedOptions,
      existingOptions,
      pathOptions,
      revivedValues,
    })

    stack[depth] = option
    stack.length = depth + 1
  }

  return { options: parsedOptions, revivedValues }
}

// Re-key a value-tracking set (selection, collapsed, new, manually-edited) when
// an option's value changes: drop the old value and add the new one, but only
// if the old value was actually tracked.
export const renameValueInSet = (
  set: Set<string>,
  oldValue: string,
  newValue: string,
): Set<string> => {
  const next = new Set(set)
  if (next.delete(oldValue)) next.add(newValue)
  return next
}

// Drop a batch of values from a value-tracking set (e.g. hard-removed options
// freeing their values, or expanding collapsed parents).
export const pruneValuesFromSet = (
  set: Set<string>,
  values: Iterable<string>,
): Set<string> => {
  const next = new Set(set)
  for (const value of values) next.delete(value)
  return next
}

export const optionMatchesQuery = (
  option: HierarchyOption,
  query: string,
): boolean =>
  !query ||
  option.label.toLowerCase().includes(query) ||
  option.value.toLowerCase().includes(query)

export const otherOptionValues = (
  options: HierarchyOption[],
  index: number,
): Set<string> =>
  new Set(
    options
      .filter((_, currentIndex) => currentIndex !== index)
      .map(option => option.value),
  )

export const buildAllExpandedRows = (
  options: HierarchyOption[],
): HierarchyRow[] =>
  flattenHierarchyRows(
    options,
    new Set(
      options.filter(option => !option.deleted).map(option => option.value),
    ),
    '',
  )

const getAncestorValues = (
  value: string,
  optionsByValue: Map<string, HierarchyOption>,
): string[] => {
  const ancestors: string[] = []
  const visited = new Set<string>()
  let currentValue: string | undefined = value

  while (currentValue && !visited.has(currentValue)) {
    visited.add(currentValue)
    const option = optionsByValue.get(currentValue)
    if (!option) break

    ancestors.push(option.value)
    currentValue = option.parentValue
  }

  return ancestors
}

export const indentHierarchyOptions = (
  options: HierarchyOption[],
  selectedValues: Set<string>,
  rows: HierarchyRow[],
): { options: HierarchyOption[]; parentValuesToExpand: string[] } => {
  const updatedOptions = [...options]
  const parentValuesToExpand: string[] = []

  for (const selectedValue of selectedValues) {
    const rowIndex = rows.findIndex(row => row.option.value === selectedValue)
    if (rowIndex <= 0) continue

    const selectedOptionIndex = updatedOptions.findIndex(
      option => option.value === selectedValue,
    )
    if (selectedOptionIndex === -1) continue

    // Skip when this node's parent is also selected: the parent moves and this
    // node rides along beneath it, so indenting it independently would detach it
    // from the moving subtree.
    const parentValue = updatedOptions[selectedOptionIndex].parentValue
    if (parentValue && selectedValues.has(parentValue)) continue

    // New parent is the nearest preceding row that is not itself selected, so a
    // selected block nests under the unselected option above it rather than
    // under one of its own moving members.
    const descendants = getDescendantValues(selectedValue, updatedOptions)
    const parentRow = [...rows]
      .slice(0, rowIndex)
      .reverse()
      .find(row => !selectedValues.has(row.option.value))

    if (
      !parentRow ||
      descendants.has(parentRow.option.value) ||
      !canPlaceSubtree(updatedOptions, selectedValue, parentRow.option.value)
    ) {
      continue
    }

    updatedOptions[selectedOptionIndex] = {
      ...updatedOptions[selectedOptionIndex],
      parentValue: parentRow.option.value,
    }
    parentValuesToExpand.push(
      ...getAncestorValues(
        parentRow.option.value,
        buildOptionsByValue(updatedOptions),
      ),
    )
  }

  return { options: updatedOptions, parentValuesToExpand }
}

export const outdentHierarchyOptions = (
  options: HierarchyOption[],
  selectedValues: Set<string>,
): HierarchyOption[] => {
  if (!selectedValues.size) return options

  const optionsByValue = buildOptionsByValue(options)

  return options.map(option => {
    const { parentValue } = option
    // A selected node outdents to its grandparent — unless its parent is also
    // selected. The parent is moving up and this node rides along beneath it,
    // so outdenting it too would detach it from the moving subtree.
    if (
      !selectedValues.has(option.value) ||
      !parentValue ||
      selectedValues.has(parentValue)
    ) {
      return option
    }

    const parent = optionsByValue.get(parentValue)
    return { ...option, parentValue: parent?.parentValue }
  })
}

export const deleteHierarchyOptions = (
  options: HierarchyOption[],
  selectedValues: Set<string>,
): { options: HierarchyOption[]; freedValues: string[] } => {
  const valuesToDelete = new Set<string>()
  selectedValues.forEach(value => {
    valuesToDelete.add(value)
    getDescendantValues(value, options).forEach(descendantValue =>
      valuesToDelete.add(descendantValue),
    )
  })

  const updatedOptions = options
    .filter(option => !(valuesToDelete.has(option.value) && option.isNew))
    .map(option =>
      valuesToDelete.has(option.value) ? { ...option, deleted: true } : option,
    )

  // Hard-removed new options free their value for reuse, so the caller drops
  // those values from its value-keyed tracking sets — otherwise a later option
  // that normalizes to the same value inherits stale collapsed/manual-edit/new
  // state. Archived (saved) options keep their value reserved, so are left.
  const freedValues = options
    .filter(option => valuesToDelete.has(option.value) && option.isNew)
    .map(option => option.value)

  return { options: updatedOptions, freedValues }
}

// Whether applying nextOptions would turn a saved, record-using leaf into a
// parent — which is disallowed because existing records point at it as a value.
// Brand-new options carry no records, and options that were already parents are
// not a fresh conversion. While usage is still loading, any new conversion is
// blocked to stay on the safe side.
export const isBlockedNestingConversion = (
  previousOptions: HierarchyOption[],
  nextOptions: HierarchyOption[],
  usedOptionValues: Set<string>,
  isUsageUnknown: boolean,
): boolean => {
  const previousParents = getParentValues(previousOptions)
  const nextParents = getParentValues(nextOptions)
  const optionByValue = new Map(
    nextOptions.map(option => [option.value, option]),
  )
  return [...nextParents].some(value => {
    if (previousParents.has(value)) return false
    const option = optionByValue.get(value)
    if (!option || option.isNew) return false
    return isUsageUnknown || usedOptionValues.has(value)
  })
}

// Restore a deleted option that a freshly typed new option collides with: drop
// the new option, un-delete the matched option, and adopt the new option's
// children. Returns null when the merged subtree would exceed the depth limit.
export const reviveHierarchyOption = (
  options: HierarchyOption[],
  newOptionIndex: number,
  deletedOption: HierarchyOption,
): HierarchyOption[] | null => {
  const newOption = options[newOptionIndex]

  // Delete cascades to descendants, so a deleted ancestor means the revived
  // option's whole original chain is gone. Re-home it onto the new option's
  // (live) position; otherwise getVisibleOptions would hide it after save. When
  // the original parent is still live, keep the option where it was.
  const originalParent = deletedOption.parentValue
    ? options.find(option => option.value === deletedOption.parentValue)
    : undefined
  const originalParentGone =
    !!deletedOption.parentValue && (!originalParent || !!originalParent.deleted)
  const revivedParentValue = originalParentGone
    ? newOption.parentValue
    : deletedOption.parentValue

  const revived = options
    .filter((_, currentIndex) => currentIndex !== newOptionIndex)
    .map(currentOption => {
      if (currentOption.value === deletedOption.value) {
        return {
          ...currentOption,
          deleted: false,
          parentValue: revivedParentValue,
        }
      }
      if (currentOption.parentValue === newOption.value) {
        return { ...currentOption, parentValue: deletedOption.value }
      }
      return currentOption
    })

  // Reparenting the new option's children onto the (possibly nested) revived
  // option can push the merged subtree past the depth limit.
  if (!canPlaceSubtree(revived, deletedOption.value, revivedParentValue)) {
    return null
  }

  return revived
}

export const reorderHierarchyOptions = ({
  options,
  expandedValues,
  searchTerm,
  oldIndex,
  newIndex,
}: {
  options: HierarchyOption[]
  expandedValues: Set<string>
  searchTerm: string
  oldIndex: number
  newIndex: number
}): { options: HierarchyOption[]; parentValueToExpand?: string } | null => {
  if (oldIndex === newIndex) return null

  // Drag indices address the visible list, while the full all-expanded tree is
  // needed to relocate collapsed descendants and read true parent depth. Both
  // derive from options, so deriving them here keeps callers from passing
  // mismatched row views.
  const rows = flattenHierarchyRows(options, expandedValues, searchTerm)
  const allRows = buildAllExpandedRows(options)

  const visibleMovingRow = rows[oldIndex]
  const visibleTargetRow = rows[newIndex]
  if (!visibleMovingRow || !visibleTargetRow) return null

  const movingRow = allRows.find(
    row => row.option.value === visibleMovingRow.option.value,
  )
  if (!movingRow) return null

  const movingValues = getDescendantValues(movingRow.option.value, options)
  movingValues.add(movingRow.option.value)
  if (movingValues.has(visibleTargetRow.option.value)) return null

  const remainingVisibleRows = rows.filter(
    row => !movingValues.has(row.option.value),
  )
  const targetRemainingIndex = remainingVisibleRows.findIndex(
    row => row.option.value === visibleTargetRow.option.value,
  )
  if (targetRemainingIndex < 0) return null

  const isMovingDown = newIndex > oldIndex
  const aboveRow = isMovingDown
    ? visibleTargetRow
    : remainingVisibleRows[targetRemainingIndex - 1]
  const belowRow = isMovingDown
    ? remainingVisibleRows[targetRemainingIndex + 1]
    : visibleTargetRow
  const nextParentValue =
    belowRow?.option.parentValue ?? aboveRow?.option.parentValue

  if (nextParentValue && movingValues.has(nextParentValue)) return null

  if (!canPlaceSubtree(options, movingRow.option.value, nextParentValue)) {
    return null
  }

  const movingOptions = options.filter(option => movingValues.has(option.value))
  const remainingOptions = options.filter(
    option => !movingValues.has(option.value),
  )
  const updatedMovingOptions = movingOptions.map(option =>
    option.value === movingRow.option.value
      ? { ...option, parentValue: nextParentValue }
      : option,
  )
  const insertIndex = belowRow
    ? remainingOptions.findIndex(
        option => option.value === belowRow.option.value,
      )
    : remainingOptions.length

  if (insertIndex < 0) return null

  return {
    options: [
      ...remainingOptions.slice(0, insertIndex),
      ...updatedMovingOptions,
      ...remainingOptions.slice(insertIndex),
    ],
    parentValueToExpand: nextParentValue,
  }
}

export const flattenHierarchyRows = (
  options: HierarchyOption[],
  expandedValues: Set<string>,
  searchTerm: string,
): HierarchyRow[] => {
  const visibleOptions = options
    .map((option, index) => ({ option, index }))
    .filter(({ option }) => !option.deleted)
  const optionsByValue = buildOptionsByValue(
    visibleOptions.map(({ option }) => option),
  )
  const childrenByParent = new Map<ParentKey, HierarchyRow[]>()
  const query = searchTerm.trim().toLowerCase()

  visibleOptions.forEach(({ option, index }) => {
    const parentValue = resolveParentKey(option, optionsByValue)
    const children = childrenByParent.get(parentValue) || []
    children.push({ option, index, depth: 0, hasChildren: false })
    childrenByParent.set(parentValue, children)
  })

  const rows: HierarchyRow[] = []
  const visited = new Set<string>()

  const visit = (row: HierarchyRow, depth: number): HierarchyRow[] => {
    if (visited.has(row.option.value)) return []

    visited.add(row.option.value)
    const children = childrenByParent.get(row.option.value) || []
    const canShowChildren = query || expandedValues.has(row.option.value)
    const childRows = canShowChildren
      ? children.flatMap(child => visit(child, depth + 1))
      : []
    const matchesQuery = optionMatchesQuery(row.option, query)

    if (!matchesQuery && childRows.length === 0) return []

    return [
      {
        ...row,
        depth,
        hasChildren: children.length > 0,
      },
      ...childRows,
    ]
  }

  for (const root of childrenByParent.get(ROOT_PARENT) || []) {
    rows.push(...visit(root, 0))
  }

  return rows
}

/**
 * Whether `next` places any option under a different parent than `previous`
 * does. A fixed vocabulary - the location type list - may be reordered among
 * siblings but not restructured, so a drag that crosses levels is refused.
 */
export const changesHierarchyParents = (
  previous: HierarchyOption[],
  next: HierarchyOption[],
): boolean => {
  const previousParents = new Map(
    previous.map(option => [option.value, option.parentValue ?? '']),
  )

  return next.some(
    option =>
      previousParents.has(option.value) &&
      previousParents.get(option.value) !== (option.parentValue ?? ''),
  )
}
